import React, { useMemo } from 'react';
import './Pagination.scss';

type PageItem =
  | { type: 'page'; page: number; current: boolean }
  | { type: 'ellipsis'; key: string }
  | { type: 'control'; key: 'first' | 'prev' | 'next' | 'last'; disabled: boolean };

interface PaginationProps {
  page: number; // поточна сторінка (1..N)
  total: number; // загальна кількість елементів
  perPage: number; // елементів на сторінку
  onChange: (page: number) => void;
  siblingCount?: number; // скільки сусідніх сторінок показувати (по обидва боки)
  boundaryCount?: number; // скільки крайніх сторінок показувати зліва/справа
  className?: string;
}

export function Pagination({
  page,
  total,
  perPage,
  onChange,
  siblingCount = 1,
  boundaryCount = 1,
  className = '',
}: PaginationProps): JSX.Element {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const cl = ['pagination', className].filter(Boolean).join(' ');

  const items: PageItem[] = useMemo(() => {
    const items: PageItem[] = [];

    const startPages = range(1, Math.min(boundaryCount, totalPages));
    const endPages = range(Math.max(totalPages - boundaryCount + 1, boundaryCount + 1), totalPages);

    const leftSiblingStart = Math.max(
      Math.min(page - siblingCount, totalPages - boundaryCount - siblingCount * 2 - 1),
      boundaryCount + 2,
    );
    const rightSiblingEnd = Math.min(
      Math.max(page + siblingCount, boundaryCount + siblingCount * 2 + 2),
      endPages.length ? endPages[0] - 2 : totalPages - 1,
    );

    // controls
    items.push({ type: 'control', key: 'first', disabled: page === 1 });
    items.push({ type: 'control', key: 'prev', disabled: page === 1 });

    // start pages
    startPages.forEach((p) => items.push({ type: 'page', page: p, current: p === page }));

    // left ellipsis
    if (leftSiblingStart > boundaryCount + 2) {
      items.push({ type: 'ellipsis', key: 'left' });
    } else if (boundaryCount + 1 < totalPages - boundaryCount) {
      const p = boundaryCount + 1;
      items.push({ type: 'page', page: p, current: p === page });
    }

    // middle range
    for (let p = leftSiblingStart; p <= rightSiblingEnd; p++) {
      items.push({ type: 'page', page: p, current: p === page });
    }

    // right ellipsis
    if (rightSiblingEnd < totalPages - boundaryCount - 1) {
      items.push({ type: 'ellipsis', key: 'right' });
    } else if (totalPages - boundaryCount > boundaryCount) {
      const p = totalPages - boundaryCount;
      if (p >= 1 && p > (items.at(-1) as any)?.page) {
        items.push({ type: 'page', page: p, current: p === page });
      }
    }

    // end pages
    endPages.forEach((p) => items.push({ type: 'page', page: p, current: p === page }));

    // controls
    items.push({ type: 'control', key: 'next', disabled: page === totalPages });
    items.push({ type: 'control', key: 'last', disabled: page === totalPages });

    return items;
  }, [page, total, perPage, siblingCount, boundaryCount, totalPages]);

  const go = (target: number) => {
    const clamped = Math.max(1, Math.min(totalPages, target));
    if (clamped !== page) onChange(clamped);
  };

  const onControl = (key: PageItem & { type: 'control' }) => {
    if (key.disabled) return;
    if (key.key === 'first') go(1);
    if (key.key === 'prev') go(page - 1);
    if (key.key === 'next') go(page + 1);
    if (key.key === 'last') go(totalPages);
  };

  return (
    <nav className={cl} role="navigation" aria-label="Pagination">
      <ul className="pagination__list">
        {items.map((it, idx) => {
          if (it.type === 'ellipsis') {
            return (
              <li key={`${it.key}-${idx}`} className="pagination__item">
                <span className="pagination__ellipsis" aria-hidden="true">
                  …
                </span>
              </li>
            );
          }

          if (it.type === 'control') {
            const label =
              it.key === 'first'
                ? 'First page'
                : it.key === 'prev'
                ? 'Previous page'
                : it.key === 'next'
                ? 'Next page'
                : 'Last page';
            const icon =
              it.key === 'first' ? '⟪' : it.key === 'prev' ? '‹' : it.key === 'next' ? '›' : '⟫';

            return (
              <li key={`${it.key}-${idx}`} className="pagination__item">
                <button
                  type="button"
                  className="pagination__btn pagination__btn--control"
                  aria-label={label}
                  disabled={it.disabled}
                  onClick={() => onControl(it)}
                >
                  {icon}
                </button>
              </li>
            );
          }

          // page
          return (
            <li key={it.page} className="pagination__item">
              <button
                type="button"
                className={`pagination__btn ${it.current ? 'is-current' : ''}`}
                aria-current={it.current ? 'page' : undefined}
                aria-label={`Page ${it.page}`}
                onClick={() => go(it.page)}
              >
                {it.page}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function range(start: number, end: number): number[] {
  const out: number[] = [];
  for (let i = start; i <= end; i++) out.push(i);
  return out;
}
